/**
 * Firebase SDK wiring. All three VITE_FIREBASE_* values are Firebase public
 * web config — baked into the client bundle at Vite build time. Security is
 * enforced by Firestore security rules + Firebase Auth, not by hiding keys.
 * See README for details.
 */

import { initializeApp, getApps } from "firebase/app";
import { getAuth, initializeAuth, connectAuthEmulator, setPersistence, browserLocalPersistence } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";

// In test mode, use placeholder values so Firebase SDK initializes without error.
// Tests mock Firestore/Auth at the module level — they never make real network calls.
const isTest = import.meta.env.MODE === "test";

// Set by scripts/dev-local.mjs via .env.local — routes Auth + Firestore to
// the local emulator suite instead of production. `npm run dev` never sets
// this, so production-connected dev is unaffected.
const useEmulator = import.meta.env.VITE_USE_EMULATOR === "true";

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? (isTest || useEmulator ? "demo-api-key" : undefined),
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? (isTest || useEmulator ? "demo-relay-local.firebaseapp.com" : undefined),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? (isTest || useEmulator ? "demo-relay-local" : undefined),
};

if (!config.apiKey || !config.authDomain || !config.projectId) {
  if (import.meta.env.DEV) {
    throw new Error(
      "[firebase] VITE_FIREBASE_* env vars missing. Refusing to connect to prod Firebase in dev mode. " +
      "Create .env.local with the required values — see README.",
    );
  }
  // In prod the build-time substitution in cloudbuild.yaml guarantees these are set.
  console.warn(
    "[firebase] VITE_FIREBASE_* env vars missing. Auth + Firestore will fail until env vars are set.",
  );
}

export const app = getApps().length ? getApps()[0] : initializeApp(config);

// backlog #35460-followup — getAuth(app) always initializes the default
// browser popup-redirect resolver (gapi.iframes-based, needed for
// signInWithPopup/signInWithRedirect), which eagerly opens a cross-origin
// iframe against `authDomain` and waits on a postMessage handshake with it.
// Against the local emulator's fake authDomain, that handshake never
// completes in headless Chromium — confirmed via network tracing:
// signInWithEmailAndPassword's actual REST call is never even issued, the
// SDK hangs forever inside its own resolver init first. initializeAuth()
// with no popupRedirectResolver skips that entirely; safe here because the
// Auth emulator doesn't support real Google OAuth anyway (README), so
// signInWithGoogle is never exercised in emulator mode regardless.
// Production (useEmulator false) is untouched — getAuth(app) still wires
// up the default resolver there, same as before.
export const auth = useEmulator
  ? initializeAuth(app, { persistence: browserLocalPersistence })
  : getAuth(app);
export const db = getFirestore(app);
// track:hgv-verification (seq #42130) — HGV evidence uploads are the first
// thing in this codebase to use Firebase Storage; nothing else touches it.
export const storage = getStorage(app);

if (useEmulator) {
  connectFirestoreEmulator(db, "localhost", 8085);
  connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
  connectStorageEmulator(storage, "localhost", 9199);
  console.log("[firebase] Connected to local emulators (Firestore :8085, Auth :9099, Storage :9199)");
} else {
  // Force localStorage-based persistence instead of the SDK's default
  // IndexedDB rehydration path. Opening + reading IndexedDB on cold load is
  // measurably slower than a synchronous localStorage read, which delays
  // the first onAuthStateChanged callback (and everything gated on it) by
  // seconds on some browsers. Fire-and-forget: must not throw on browsers/
  // modes (e.g. private browsing) that restrict storage. The emulator path
  // above already gets browserLocalPersistence directly via initializeAuth,
  // so this only needs to run for the getAuth(app) (production) branch.
  setPersistence(auth, browserLocalPersistence).catch(() => {});
}
